import React, { Component, type ReactNode } from 'react';

import noop from 'lodash/noop';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import {
	DragDropContext,
	type BeforeCapture,
	type DragStart,
	type DragUpdate,
	type DropResult,
	type ResponderProvided,
	type Sensor,
} from 'react-beautiful-dnd';
import { injectIntlV2 as injectIntl, type IntlShapeV2 as IntlShape } from '@atlassian/jira-intl';
import {
	CARD_DND_TYPE,
	COLUMN_DND_TYPE,
	getDraggingCardStatus,
	getDescriptor,
	type CardId,
	type ColumnId,
	type SwimlaneId,
} from '@atlassian/jira-platform-board-kit';

import messages from './messages';

const IS_DRAGGING_CLASS_NAME = 'ak-dnd-is-dragging';

// Note that react-beautiful-dnd actually handles body styles when dragging
// BUT it doesn't prevent the ICC placeholder from triggering on the board and backlog
// and as a result we need this class here to prevent that behaviour
const StyleOverride = () => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
	<style
		// eslint-disable-next-line react/no-danger
		dangerouslySetInnerHTML={{
			__html: `
                /* disable hover events during drag */
                body.${IS_DRAGGING_CLASS_NAME} * {
                    pointer-events: none;
                }
            `,
		}}
	/>
);

type Props = {
	children: ReactNode;
	cardDndTypes: string[];
	sensors?: Sensor[];
	onCardsMove: (
		cardIds: CardId[],
		sourceColumnId: ColumnId,
		destinationColumnId: ColumnId,
		sourceCardIndex: number,
		destinationCardIndex: number,
		sourceSwimlaneId: SwimlaneId | undefined,
		destinationSwimlaneId: SwimlaneId | undefined,
	) => void;
	onColumnMove: (
		columnId: ColumnId,
		sourceColumnIndex: number,
		destinationColumnIndex: number,
	) => void;
	onBeforeCapture: (beforeCapture?: BeforeCapture) => void;
	onDragStart: (dragStart?: DragStart) => void;
	onDragUpdate: (dragUpdate?: DragUpdate) => void;
	onDragEnd: (dragStop?: DropResult) => void;
	intl?: IntlShape;
};

const defaultCardDndTypes = [CARD_DND_TYPE];

// eslint-disable-next-line jira/react/no-class-components
class BoardDragDropContext extends Component<Props> {
	static defaultProps = {
		cardDndTypes: defaultCardDndTypes,
		onCardsMove: noop,
		onColumnMove: noop,
		onBeforeCapture: noop,
		onDragStart: noop,
		onDragUpdate: noop,
		onDragEnd: noop,
	};

	draggingCardRef?: Element;

	initialTotalCards = 0;

	onBeforeCapture = (beforeCapture?: BeforeCapture) => {
		this.props.onBeforeCapture(beforeCapture);
	};

	onBeforeDragStart = (dragStart: DragStart) => {
		const card = document.querySelector(`[data-rbd-draggable-id="${dragStart.draggableId}"]`);
		if (card && card.classList) this.draggingCardRef = card;

		const { totalCards } = getDraggingCardStatus(
			dragStart.source.droppableId,
			this.draggingCardRef,
		);

		this.initialTotalCards = totalCards;
	};

	onDragStart = (dragStart: DragStart, provided: ResponderProvided) => {
		this.props.onDragStart(dragStart);
		document.body?.classList.add(IS_DRAGGING_CLASS_NAME);
		if (!this.props.intl) return;

		const { intl } = this.props;
		// Clear the unwanted screen reader announce from the dragging item
		document
			.querySelector(`[data-rbd-draggable-id="${dragStart.draggableId}"]`)
			?.setAttribute(
				'aria-labelledby',
				this.draggingCardRef?.getAttribute('aria-labelledby') ?? '',
			);

		const { columnName, issueKey, ignoreDndTypeInMessage } = getDraggingCardStatus(
			dragStart.source.droppableId,
			this.draggingCardRef,
		);
		provided.announce(
			intl.formatMessage(messages.grabbed, {
				issueKey,
				fromPosition: dragStart.source.index + 1,
				totalCards: this.initialTotalCards,
				columnName,
				dndType: dragStart.type,
				ignoreDndTypeInMessage,
			}),
		);
	};

	onDragUpdate = (dragUpdate: DragUpdate, provided: ResponderProvided) => {
		this.props.onDragUpdate(dragUpdate);
		if (!this.props.intl) return;

		const { intl } = this.props;
		const { source, destination } = dragUpdate;
		const { cardDndTypes } = this.props;
		if (cardDndTypes.includes(dragUpdate.type)) {
			const { columnName, issueKey, ignoreDndTypeInMessage } = getDraggingCardStatus(
				destination?.droppableId ?? source.droppableId,
				this.draggingCardRef,
			);

			if (destination?.droppableId === source.droppableId) {
				if (destination?.index === source.index) {
					provided.announce(
						intl.formatMessage(messages.movedBackPosition, {
							issueKey,
							columnName,
							ignoreDndTypeInMessage,
						}),
					);
				} else {
					provided.announce(
						intl.formatMessage(messages.movedNewPosition, {
							issueKey,
							fromPosition: source.index + 1,
							toPosition: (destination.index ?? 0) + 1,
							totalCards: this.initialTotalCards,
							columnName,
							ignoreDndTypeInMessage,
						}),
					);
				}
				return;
			}
			provided.announce(
				intl.formatMessage(messages.movedNewColumn, {
					issueKey,
					toPosition: (dragUpdate.destination?.index ?? 0) + 1,
					columnName,
					ignoreDndTypeInMessage,
				}),
			);
		}
	};

	onDragEnd = (result: DropResult, provided: ResponderProvided) => {
		this.props.onDragEnd(result);
		document.body?.classList.remove(IS_DRAGGING_CLASS_NAME);

		const { destination } = result;

		if (result.reason === 'CANCEL' || !destination) {
			return;
		}

		const { cardDndTypes } = this.props;

		if (cardDndTypes.includes(result.type)) {
			const cardId = Number(getDescriptor(result.draggableId).id);
			const { id: sourceColumnId, swimlaneId: sourceSwimlaneId } = getDescriptor(
				result.source.droppableId,
			);
			const { id: destinationColumnId, swimlaneId: destinationSwimlaneId } = getDescriptor(
				destination.droppableId,
			);

			this.props.onCardsMove(
				[cardId],
				sourceColumnId,
				destinationColumnId,
				result.source.index,
				destination.index,
				sourceSwimlaneId,
				destinationSwimlaneId,
			);

			// Temporarily clear the lift instructions so drop message is announced first
			const labelledby = this.draggingCardRef?.getAttribute('aria-labelledby') || '';
			this.draggingCardRef?.setAttribute('aria-labelledby', '');

			const { columnName, issueKey, totalCards, ignoreDndTypeInMessage } = getDraggingCardStatus(
				destination.droppableId ?? result.source.droppableId,
				this.draggingCardRef,
			);
			if (this.props.intl) {
				const sameColumn = sourceColumnId === destinationColumnId;
				provided.announce(
					this.props.intl.formatMessage(messages.dropped, {
						issueKey,
						toPosition: (destination.index ?? 0) + 1,
						totalCards: sameColumn ? this.initialTotalCards : totalCards + 1,
						columnName,
						ignoreDndTypeInMessage,
					}),
				);
			}

			this.draggingCardRef?.setAttribute('aria-labelledby', labelledby);
		} else if (result.type === COLUMN_DND_TYPE) {
			const columnId = getDescriptor(result.draggableId).id;
			this.props.onColumnMove(columnId, result.source.index, destination.index);
		}
		this.draggingCardRef = undefined;
		this.initialTotalCards = 0;
	};

	render() {
		const { children, sensors } = this.props;
		return (
			<DragDropContext
				onBeforeCapture={this.onBeforeCapture}
				onBeforeDragStart={this.onBeforeDragStart}
				onDragStart={this.onDragStart}
				onDragUpdate={this.onDragUpdate}
				onDragEnd={this.onDragEnd}
				sensors={sensors}
			>
				<StyleOverride />
				{children}
			</DragDropContext>
		);
	}
}

export default injectIntl(BoardDragDropContext);
